import { motion } from 'framer-motion';
import { ProgressBar } from '../atoms/ProgressBar';
import { theme } from '../../theme';
import { debtCard as copy } from '../../language';
import { useMobile } from '../../hooks/useMediaQuery';

interface DebtCardProps {
  name: string;
  balance: number;
  originalBalance: number;
  interestRate: number;
  minimumPayment: number;
  priority?: 'high' | 'medium' | 'low';
  onPaymentClick?: () => void;
}

export const DebtCard = ({
  name, balance, originalBalance, interestRate, minimumPayment,
  priority = 'medium', onPaymentClick,
}: DebtCardProps) => {
  const mobile = useMobile();
  const paidOff = originalBalance - balance;
  const pct = (paidOff / originalBalance) * 100;
  const borderColor = theme.priorityColors[priority];
  const progressColor = pct > 66 ? 'success' : pct > 33 ? 'warning' : 'danger';

  const priorityLabel: Record<string, string> = copy.priority;

  return (
    <motion.div
      initial={{ opacity: 0, x: 12 }}
      animate={{ opacity: 1, x: 0 }}
      onClick={onPaymentClick}
      style={{
        background: theme.semantic.bg.surface,
        borderLeft: `${theme.borders.heavy} ${borderColor}`,
        padding: mobile ? theme.spacing[4] : theme.spacing[5],
        marginBottom: theme.spacing[3],
        cursor: onPaymentClick ? 'pointer' : 'default',
      }}
    >
      {/* Header row */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing[4],
      }}>
        <h3 style={{
          fontFamily: theme.typography.fontFamily.display,
          fontSize: mobile ? theme.typography.fontSize.base : theme.typography.fontSize.lg,
          fontWeight: theme.typography.fontWeight.bold,
          color: theme.semantic.text.primary,
          letterSpacing: theme.typography.letterSpacing.wide,
          textTransform: 'uppercase' as const,
          margin: 0,
        }}>
          {name}
        </h3>
        <span style={{
          fontFamily: theme.typography.fontFamily.display,
          fontSize: theme.typography.fontSize.xs,
          fontWeight: theme.typography.fontWeight.bold,
          color: borderColor,
          letterSpacing: theme.typography.letterSpacing.widest,
          padding: `${theme.spacing[1]} ${theme.spacing[3]}`,
          border: `${theme.borders.medium} ${borderColor}`,
        }}>
          {priorityLabel[priority]}
        </span>
      </div>

      {/* Stats row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: mobile ? theme.spacing[2] : theme.spacing[4],
        marginBottom: theme.spacing[4],
      }}>
        {[
          { label: copy.stats.balance, val: `£${balance.toLocaleString()}`, color: theme.brand.crimson },
          { label: copy.stats.apr, val: `${interestRate}%`, color: theme.brand.copper },
          { label: copy.stats.paidOff, val: `£${paidOff.toLocaleString()}`, color: theme.colors.success },
        ].map(({ label, val, color }) => (
          <div key={label}>
            <div style={{
              fontFamily: theme.typography.fontFamily.display,
              fontSize: '0.65rem',
              fontWeight: theme.typography.fontWeight.bold,
              color: theme.semantic.text.muted,
              letterSpacing: theme.typography.letterSpacing.widest,
              marginBottom: theme.spacing[1],
            }}>
              {label}
            </div>
            <div style={{
              fontFamily: theme.typography.fontFamily.mono,
              fontSize: mobile ? theme.typography.fontSize.lg : theme.typography.fontSize.xl,
              fontWeight: theme.typography.fontWeight.bold,
              color,
            }}>
              {val}
            </div>
          </div>
        ))}
      </div>

      {/* Progress */}
      <ProgressBar
        value={paidOff}
        max={originalBalance}
        label={copy.payment(minimumPayment)}
        color={progressColor}
        height="sm"
      />
    </motion.div>
  );
};

export default DebtCard;
